(() => {
  if (customElements.get('soi-service-reviews')) return;
  const svgNS = 'http://www.w3.org/2000/svg';
  const starPath = 'M12 1.4l3.27 6.63 7.32 1.06-5.3 5.17 1.25 7.3L12 18.11l-6.54 3.45 1.25-7.3-5.3-5.17 7.32-1.06Z';
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const safePhoto = (url) => {
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'https:' && parsed.hostname === 'attachments.etrusted.com' ? parsed.href : null;
    } catch (_) { return null; }
  };
  const textValue = (value) => typeof value === 'string' ? value.trim() : '';
  let starId = 0;
  const stars = (rating) => {
    const wrap = element('span', 'soi-reviews__stars');
    wrap.setAttribute('aria-hidden', 'true');
    for (let index = 0; index < 5; index++) {
      const svg = document.createElementNS(svgNS, 'svg');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('focusable', 'false');
      const background = document.createElementNS(svgNS, 'path');
      background.setAttribute('d', starPath);
      background.setAttribute('fill', '#d9dce0');
      svg.append(background);
      const fraction = Math.max(0, Math.min(1, rating - index));
      if (fraction) {
        const fill = document.createElementNS(svgNS, 'path');
        fill.setAttribute('d', starPath);
        fill.setAttribute('fill', 'currentColor');
        if (fraction < 1) {
          const clip = document.createElementNS(svgNS, 'clipPath');
          clip.id = `soi-review-star-${++starId}`;
          const rect = document.createElementNS(svgNS, 'rect');
          rect.setAttribute('width', String(24 * fraction));
          rect.setAttribute('height', '24');
          clip.append(rect);
          svg.append(clip);
          fill.setAttribute('clip-path', `url(#${clip.id})`);
        }
        svg.append(fill);
      }
      wrap.append(svg);
    }
    return wrap;
  };
  class ServiceReviews extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.abort = new AbortController();
      this.page = 0;
      this.expanded = new Set();
      this.grid = this.querySelector('[data-review-grid]');
      this.pages = this.querySelector('[data-review-pages]');
      this.dialog = this.querySelector('dialog');
      try {
        this.copy = JSON.parse(this.querySelector('[data-review-translations]').textContent);
        this.data = JSON.parse(this.querySelector('[data-soi-review-data]').textContent);
        this.locale = this.dataset.locale === 'de' ? 'de-DE' : 'en-GB';
        const seen = new Set();
        this.reviews = (Array.isArray(this.data.reviews) ? this.data.reviews : []).filter(review => {
          if (!review || ![4, 5].includes(review.rating) || review.status === 'REJECTED' || !textValue(review.comment) || !review.id || seen.has(review.id) || !Number.isFinite(Date.parse(review.date))) return false;
          seen.add(review.id);
          return true;
        }).sort((a, b) => Date.parse(b.date) - Date.parse(a.date)).slice(0, 24);
        this.cards = new Map([...this.grid.querySelectorAll('[data-review-id]')].map(card => [card.dataset.reviewId, card]));
        const activeIds = new Set(this.reviews.map(review => review.id));
        for (const [id, card] of this.cards) if (!activeIds.has(id)) { card.remove(); this.cards.delete(id); }
        for (const review of this.reviews) {
          if (!this.cards.has(review.id)) {
            const card = this.renderCard(review);
            this.cards.set(review.id, card);
            this.grid.append(card);
          }
          const date = this.cards.get(review.id).querySelector('time');
          date.textContent = new Date(review.date).toLocaleDateString(this.locale, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' });
        }
        this.renderSummary();
        this.renderPage();
        this.grid.setAttribute('aria-busy', 'false');
        this.querySelector('[data-review-error]').hidden = this.reviews.length > 0;
      } catch (_) {
        this.grid.setAttribute('aria-busy', 'false');
        this.querySelector('[data-review-error]').hidden = false;
        return;
      }
      this.addEventListener('click', event => this.handleClick(event), { signal: this.abort.signal });
      this.dialog.addEventListener('close', () => {
        if (this.previousOverflow !== undefined) document.body.style.overflow = this.previousOverflow;
        this.previousOverflow = undefined;
        this.photoTrigger?.focus({ preventScroll: true });
      }, { signal: this.abort.signal });
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }, { signal: this.abort.signal });
      this.dialog.addEventListener('keydown', event => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.showPhoto(this.photoIndex - 1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); this.showPhoto(this.photoIndex + 1); }
      }, { signal: this.abort.signal });
      this.resizeObserver = new ResizeObserver(entries => {
        const width = Math.round(entries[0].contentRect.width);
        if (width === this.lastWidth) return;
        this.lastWidth = width;
        cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = requestAnimationFrame(() => this.fitComments());
      });
      this.resizeObserver.observe(this.grid);
      document.fonts?.ready.then(() => { if (this.isConnected) this.fitComments(); });
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.resizeFrame);
      if (this.dialog?.open) this.dialog.close();
      if (this.previousOverflow !== undefined) document.body.style.overflow = this.previousOverflow;
      this.started = false;
    }
    renderSummary() {
      const rating = Number(this.data.rating?.average);
      const count = Number(this.data.rating?.count);
      if (!Number.isFinite(rating) || rating < 0 || rating > 5 || !Number.isSafeInteger(count) || count < 0) throw new Error('Invalid summary');
      this.querySelector('[data-review-score]').textContent = rating.toLocaleString(this.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const overallStars = this.querySelector('[data-review-stars]');
      overallStars.replaceChildren(...stars(rating).childNodes);
      this.querySelector('[data-review-count]').textContent = `${count.toLocaleString(this.locale)} ${this.copy.reviews}`;
      const certified = this.data.certificate?.valid === true;
      this.querySelector('[data-review-valid]').hidden = !certified;
      this.querySelector('[data-review-protection]').hidden = !certified;
      this.dataset.updatedAt = this.data.updatedAt || '';
    }
    renderPage() {
      const rows = this.reviews.slice(this.page * 4, this.page * 4 + 4);
      const visibleIds = new Set(rows.map(review => review.id));
      for (const [id, card] of this.cards) card.hidden = !visibleIds.has(id);
      this.pages.replaceChildren();
      const count = Math.ceil(this.reviews.length / 4);
      if (count > 1) for (let page = 0; page < count; page++) {
        const button = element('button', '', String(page + 1));
        button.type = 'button';
        button.dataset.reviewPage = String(page);
        button.setAttribute('aria-label', `${this.copy.page} ${page + 1}`);
        if (page === this.page) button.setAttribute('aria-current', 'page');
        this.pages.append(button);
      }
      this.fitComments();
    }
    renderCard(review) {
      const card = element('article', 'soi-reviews__card');
      card.dataset.reviewId = review.id;
      const top = element('div', 'soi-reviews__card-top');
      const rating = element('span', 'soi-reviews__rating-accessible');
      rating.setAttribute('role', 'img');
      rating.setAttribute('aria-label', `${review.rating} ${this.copy.outOf}`);
      rating.append(stars(review.rating));
      const date = element('time', 'soi-reviews__date', new Date(review.date).toLocaleDateString(this.locale, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' }));
      date.dateTime = review.date;
      top.append(rating, date);
      card.append(top);
      const title = textValue(review.title);
      if (title) card.append(element('h3', 'soi-reviews__title', title));
      const comment = element('p', 'soi-reviews__comment');
      comment.id = `soi-review-text-${this.id}-${review.id}`;
      comment.dataset.fullText = textValue(review.comment);
      card.append(comment);
      const footer = element('div', 'soi-reviews__footer');
      const identity = element('div', 'soi-reviews__identity');
      const first = textValue(review.consumer?.firstName);
      const last = [...textValue(review.consumer?.lastName)][0];
      identity.append(element('span', 'soi-reviews__name', first ? `${first}${last ? ` ${last}.` : ''}` : this.copy.anonymous));
      if (review.isVerified === true) {
        const verified = element('span', 'soi-reviews__verified');
        const icon = element('img');
        icon.src = this.dataset.verifiedIcon;
        icon.alt = '';
        icon.width = 14;
        icon.height = 14;
        verified.append(icon, element('span', 'soi-reviews__desktop', this.copy.verified), element('span', 'soi-reviews__mobile', this.copy.verifiedShort));
        identity.append(verified);
      }
      footer.append(identity);
      const images = (review.images || []).map(image => safePhoto(image.original?.url)).filter(Boolean);
      if (images.length) {
        const photos = element('div', 'soi-reviews__photos');
        const thumbnail = element('button', 'soi-reviews__thumbnail');
        thumbnail.type = 'button';
        thumbnail.dataset.reviewPhoto = review.id;
        thumbnail.setAttribute('aria-label', `${this.copy.photo}${images.length > 1 ? ` (${images.length})` : ''}`);
        thumbnail.setAttribute('aria-haspopup', 'dialog');
        const image = element('img');
        image.src = images[0];
        image.loading = 'lazy';
        image.decoding = 'async';
        image.alt = this.copy.photo;
        image.width = 116;
        image.height = 116;
        const zoom = element('span', 'soi-reviews__photo-zoom');
        zoom.setAttribute('aria-hidden', 'true');
        if (images.length > 1) zoom.textContent = `+${images.length - 1}`;
        else zoom.innerHTML = '<svg viewBox="0 0 20 20" fill="none"><circle cx="8" cy="8" r="5.5" stroke="currentColor" stroke-width="1.6"/><path d="m12 12 5 5M8 5v6M5 8h6" stroke="currentColor" stroke-width="1.6"/></svg>';
        thumbnail.append(image, zoom);
        photos.append(thumbnail);
        footer.append(photos);
      }
      card.append(footer);
      return card;
    }
    fitComments() {
      if (!this.isConnected || !this.grid?.clientWidth) return;
      const lines = Number.parseInt(getComputedStyle(this).getPropertyValue('--soi-review-lines'), 10) || 4;
      this.grid.querySelectorAll('.soi-reviews__card').forEach(card => {
        if (card.hidden) return;
        const paragraph = card.querySelector('.soi-reviews__comment');
        const full = paragraph.dataset.fullText;
        const isExpanded = this.expanded.has(card.dataset.reviewId);
        card.toggleAttribute('data-expanded', isExpanded);
        paragraph.textContent = full;
        const limit = Number.parseFloat(getComputedStyle(paragraph).lineHeight) * lines + 1;
        if (!isExpanded && paragraph.getBoundingClientRect().height <= limit) return;
        const button = element('button', 'soi-reviews__more', isExpanded ? this.copy.less : this.copy.more);
        button.type = 'button';
        button.dataset.reviewToggle = card.dataset.reviewId;
        button.setAttribute('aria-expanded', String(isExpanded));
        button.setAttribute('aria-controls', paragraph.id);
        if (isExpanded) { paragraph.append(document.createTextNode(' '), button); return; }
        const words = full.split(/\s+/);
        const text = document.createTextNode('');
        paragraph.replaceChildren(text, button);
        let low = 0;
        let high = words.length;
        while (low < high) {
          const middle = Math.ceil((low + high) / 2);
          text.textContent = `${words.slice(0, middle).join(' ')}… `;
          if (paragraph.getBoundingClientRect().height <= limit) low = middle;
          else high = middle - 1;
        }
        text.textContent = `${words.slice(0, low).join(' ')}… `;
      });
    }
    handleClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.dataset.reviewPage !== undefined) {
        const page = Number(button.dataset.reviewPage);
        if (page === this.page || page < 0 || page >= Math.ceil(this.reviews.length / 4)) return;
        this.page = page;
        this.renderPage();
        this.pages.querySelector('[aria-current="page"]')?.focus({ preventScroll: true });
      } else if (button.dataset.reviewToggle) {
        const id = button.dataset.reviewToggle;
        if (this.expanded.has(id)) this.expanded.delete(id); else this.expanded.add(id);
        this.fitComments();
        this.grid.querySelector(`[data-review-toggle="${CSS.escape(id)}"]`)?.focus({ preventScroll: true });
      } else if (button.dataset.reviewPhoto) {
        const review = this.reviews.find(review => review.id === button.dataset.reviewPhoto);
        this.photos = (review?.images || []).map(image => safePhoto(image.original?.url)).filter(Boolean);
        if (!this.photos.length) return;
        this.photoTrigger = button;
        this.showPhoto(0);
        this.previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        this.dialog.showModal();
      } else if (button.hasAttribute('data-photo-close')) this.dialog.close();
      else if (button.hasAttribute('data-photo-prev')) this.showPhoto(this.photoIndex - 1);
      else if (button.hasAttribute('data-photo-next')) this.showPhoto(this.photoIndex + 1);
    }
    showPhoto(index) {
      if (!this.photos?.length) return;
      this.photoIndex = (index + this.photos.length) % this.photos.length;
      const image = this.querySelector('[data-full-photo]');
      image.src = this.photos[this.photoIndex];
      image.alt = this.copy.photo;
      this.querySelector('[data-photo-position]').textContent = `${this.photoIndex + 1} / ${this.photos.length}`;
      this.querySelector('[data-photo-prev]').hidden = this.photos.length < 2;
      this.querySelector('[data-photo-next]').hidden = this.photos.length < 2;
      this.querySelector('[data-photo-position]').hidden = this.photos.length < 2;
    }
  }
  customElements.define('soi-service-reviews', ServiceReviews);
})();
